// AcademicPartnerships.js
import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, GraduationCap, Award } from 'lucide-react';

const AcademicPartnerships = () => {
  const countries = [
    { name: 'United States', image: 'usa.jpg' },
    { name: 'Canada', image: 'canada.jpg' },
    { name: 'United Kingdom', image: 'uk.jpg' },
    { name: 'Australia', image: 'australia.jpg' },
    { name: 'Europe', image: 'europe.jpg' }
  ];

  const highlights = [
    {
      icon: GraduationCap,
      stat: '100+',
      label: 'Partner Institutions',
      description: 'Across key study destinations'
    },
    {
      icon: Award,
      stat: '10-40%',
      label: 'Tuition Reductions',
      description: 'Available for eligible candidates'
    },
    {
      icon: MapPin,
      stat: '5',
      label: 'Global Destinations',
      description: 'Strategic partnerships worldwide'
    }
  ];

  return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-semibold text-[#202356] mb-4">
            Our Academic Partnerships and Opportunities
          </h2>
          <p className="text-gray-600 text-sm font-light max-w-3xl mx-auto">
            Notapaths has built long standing relationships with academic institutions across key study destinations. Through these partnerships, we support applications more effectively and explore available scholarship or tuition discount opportunities for candidates within our pipeline.
          </p>
        </motion.div>

        {/* Built on Trust and Experience */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-[#F5F8FA] p-8 rounded-lg mb-12 max-w-4xl mx-auto"
        >
          <h3 className="text-xl font-semibold text-[#202356] text-center mb-4">
            Built on Trust and Experience
          </h3>
          <p className="text-gray-700 text-sm font-light leading-relaxed text-center">
            Our partnerships are not just numbers. They are the result of years of building trusted relationships with academic institutions. This established network gives our candidates direct access to opportunities and streamlined application processes that come from proven institutional relationships.
          </p>
        </motion.div>

        {/* Stats Highlights */}
        <div className="grid md:grid-cols-3 gap-8 mb-12">
          {highlights.map((highlight, index) => {
            const Icon = highlight.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="inline-flex items-center justify-center w-16 h-16 bg-[#F5F8FA] rounded-full mb-4">
                  <Icon size={28} className="text-[#202356]" />
                </div>
                <div className="text-4xl font-light text-[#202356] mb-2">
                  {highlight.stat}
                </div>
                <div className="text-base font-semibold text-gray-700 mb-1">
                  {highlight.label}
                </div>
                <div className="text-sm text-gray-600 font-light">
                  {highlight.description}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Countries Grid */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <h3 className="text-xl font-semibold text-[#202356] text-center mb-8">
            Study Destinations
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {countries.map((country, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.4 + (index * 0.05) }}
                className="bg-[#F5F8FA] p-6 text-center hover:bg-white hover:shadow-md transition-all border border-transparent hover:border-[#2542D4]/20"
              >
                <div className="w-12 h-12 mx-auto mb-3 rounded-full overflow-hidden shadow-md">
                  <img 
                    src={require(`../../assets/countries/${country.image}`)} 
                    alt={country.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="text-sm font-normal text-gray-700">
                  {country.name}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Scholarship Info */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="mt-12 bg-[#F5F8FA] p-8 rounded-lg"
        >
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-lg font-semibold text-[#202356] mb-4">
              Scholarship & Tuition Discount Opportunities
            </h3>
            <p className="text-gray-600 text-sm font-light leading-relaxed mb-4">
              Where applicable, candidates may receive tuition reductions typically ranging between 10 percent and 40 percent, depending on academic profile, program, and availability.
            </p>
            <p className="text-gray-600 text-sm font-light leading-relaxed">
              These opportunities are competitive and not guaranteed. All recommendations are made transparently and based on eligibility.
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default AcademicPartnerships;
